import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "wouter";
import { format } from "date-fns";
import { Calendar, Clock, Car, MapPin, Loader2, Package, ArrowRight, ArrowLeft, Wallet, CreditCard, ShoppingCart, Star, Download, Repeat, Pencil, XCircle, Truck } from "lucide-react";
import { useOrders, useCancelOrder } from "@/hooks/use-orders";
import { useServices } from "@/hooks/use-services";
import { usePublicSettings } from "@/hooks/use-settings";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ReviewDialog } from "@/components/ReviewDialog";
import type { Order, OrderStatus, PaymentStatus } from "@/lib/types";
import { downloadInvoice } from "@/lib/invoice";
import { useToast } from "@/hooks/use-toast";
import { formatOrderAddress } from "@/lib/utils";
import { buildCartFromOrder, setReorderItems } from "@/lib/reorder";

function getStatusColor(status: OrderStatus): string {
  switch (status) {
    case 'pending':
      return 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20';
    case 'confirmed':
      return 'bg-blue-500/10 text-blue-600 border-blue-500/20';
    case 'in_progress':
      return 'bg-purple-500/10 text-purple-600 border-purple-500/20';
    case 'completed':
      return 'bg-green-500/10 text-green-600 border-green-500/20';
    case 'cancelled':
      return 'bg-red-500/10 text-red-600 border-red-500/20';
    default:
      return 'bg-muted text-muted-foreground';
  }
}

function getPaymentStatusColor(status: PaymentStatus | undefined): string {
  switch (status) {
    case 'pending':
      return 'bg-yellow-500/10 text-yellow-600 border-yellow-500/20';
    case 'paid':
      return 'bg-green-500/10 text-green-600 border-green-500/20';
    case 'failed':
      return 'bg-red-500/10 text-red-600 border-red-500/20';
    case 'expired':
      return 'bg-gray-500/10 text-gray-600 border-gray-500/20';
    default:
      return 'bg-muted text-muted-foreground';
  }
}

interface OrderCardProps {
  order: Order;
  onRateClick?: (orderId: string) => void;
  onDownload?: (id: string) => Promise<boolean>;
  onReorder?: (orderId: string) => void;
  onEditClick?: (orderId: string) => void;
  canEditActive?: boolean;
  onCancelClick?: (orderId: string) => void;
  canCancelOrder?: boolean;
  cancelCutoffMinutes?: number;
}

function OrderCard({ order, onRateClick, onDownload, onReorder, onEditClick, canEditActive, onCancelClick, canCancelOrder, cancelCutoffMinutes = 60 }: OrderCardProps) {
  const { toast } = useToast();
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';
  const [downloading, setDownloading] = useState(false);
  const canCompletePayment = order.status === 'pending' && order.paymentStatus === 'pending' && order.paymentLink;

  // For multi-item orders, use items array; for legacy single-item orders, fall back to serviceName
  const hasMultipleItems = order.items && order.items.length > 0;
  const isProductOrder = order.type === 'product';
  const serviceName = isRtl ? (order.serviceNameAr || order.serviceName) : (order.serviceNameEn || order.serviceName);
  const itemCount = hasMultipleItems ? order.items!.length : 1;

  return (
    <Card className="overflow-hidden" data-testid={`card-order-${order.id}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            {hasMultipleItems ? (
              <CardTitle className="text-lg flex items-center gap-2">
                {isProductOrder ? <Package className="h-5 w-5" /> : <ShoppingCart className="h-5 w-5" />}
                {t(isProductOrder ? "orders.productsCount" : "orders.itemsCount", { count: itemCount })}
              </CardTitle>
            ) : (
              <CardTitle className="text-lg">{serviceName}</CardTitle>
            )}
            <p className="text-sm text-muted-foreground mt-1">
              {t("orders.orderNumber")}: {order.id.slice(-8).toUpperCase()}
            </p>
          </div>
          <div className="flex flex-col gap-1 items-end">
            <Badge className={getStatusColor(order.status)}>
              {t(`orders.status.${order.status}`)}
            </Badge>
            {order.paymentStatus && (
              <Badge className={getPaymentStatusColor(order.paymentStatus)} variant="outline">
                {order.paymentMethod === 'cash' && order.paymentStatus === 'pending'
                  ? t('orders.paymentStatus.cashOnService')
                  : t(`orders.paymentStatus.${order.paymentStatus}`)}
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Multi-item order display */}
        {hasMultipleItems && (
          <div className="space-y-2 pb-3 border-b">
            {order.items!.map((item, index) => (
              <div key={index} className="flex items-center justify-between text-sm bg-muted/50 rounded-lg p-2">
                <div className="flex items-center gap-2">
                  {item.productId ? (
                    <Package className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Car className="h-4 w-4 text-muted-foreground" />
                  )}
                  <span className="font-medium">{item.serviceName}</span>
                  {item.vehicleType ? (
                    <Badge variant="outline" className="text-xs capitalize">
                      {t(`booking.${item.vehicleType}`)}
                    </Badge>
                  ) : item.sku ? (
                    <Badge variant="outline" className="text-[10px] font-mono">
                      {item.sku}
                    </Badge>
                  ) : null}
                  {item.quantity > 1 && (
                    <span className="text-muted-foreground">x{item.quantity}</span>
                  )}
                </div>
                <span className="font-medium">{item.subtotal.toFixed(2)} {t("services.currency")}</span>
              </div>
            ))}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Calendar className="h-4 w-4" />
            <span>{format(new Date(order.appointmentDate), 'MMM dd, yyyy')}</span>
          </div>
          <div className="flex items-center gap-2 text-muted-foreground">
            {isProductOrder ? <Truck className="h-4 w-4" /> : <Clock className="h-4 w-4" />}
            <span>
              {isProductOrder
                ? `${(isRtl ? order.deliveryPeriodAr : order.deliveryPeriodEn) || ''}${order.deliveryWindow ? ` (${order.deliveryWindow})` : ''}`
                : order.appointmentTime}
            </span>
          </div>
          {!hasMultipleItems && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Car className="h-4 w-4" />
              <span className="capitalize">{t(`booking.${order.vehicleType}`)}</span>
            </div>
          )}
          <div className="flex items-center gap-2 font-semibold">
            <span>{(order.totalPrice || order.price || 0).toFixed(2)} {t("services.currency")}</span>
          </div>
        </div>

        {(order.districtNameEn || order.governorateNameEn) && (
          <div className="flex items-start gap-2 text-sm text-muted-foreground pt-2 border-t">
            <MapPin className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              {isRtl
                ? `${order.districtNameAr || ''}${order.districtNameAr && order.governorateNameAr ? '، ' : ''}${order.governorateNameAr || ''}`
                : `${order.districtNameEn || ''}${order.districtNameEn && order.governorateNameEn ? ', ' : ''}${order.governorateNameEn || ''}`
              }
            </span>
          </div>
        )}

        {order.customerAddress && (
          <div className="flex items-start gap-2 text-sm text-muted-foreground">
            <span>{formatOrderAddress(order, t)}</span>
          </div>
        )}

        {/* Payment breakdown */}
        {(order.deliveryFee !== undefined && order.deliveryFee > 0) ||
         (order.discountAmount !== undefined && order.discountAmount > 0) ||
         (order.walletCreditUsed !== undefined && order.walletCreditUsed > 0) ? (
          <div className="text-xs text-muted-foreground space-y-1 pt-2 border-t">
            {hasMultipleItems && order.subtotal !== undefined && (
              <div className="flex justify-between">
                <span>{t(isProductOrder ? "orders.productsSubtotal" : "orders.servicesSubtotal")}:</span>
                <span>{order.subtotal.toFixed(2)} {t("services.currency")}</span>
              </div>
            )}
            {order.deliveryFee !== undefined && order.deliveryFee > 0 && (
              <div className="flex justify-between">
                <span>{t("orders.deliveryFee")}:</span>
                <span>{order.deliveryFee.toFixed(2)} {t("services.currency")}</span>
              </div>
            )}
            {order.discountAmount !== undefined && order.discountAmount > 0 && (
              <div className="flex justify-between text-green-600">
                <span>{t("orders.discount")}:</span>
                <span>-{order.discountAmount.toFixed(2)} {t("services.currency")}</span>
              </div>
            )}
            {order.walletCreditUsed !== undefined && order.walletCreditUsed > 0 && (
              <div className="flex justify-between text-primary">
                <span className="flex items-center gap-1">
                  <Wallet className="h-3 w-3" />
                  {t("orders.walletCredit")}:
                </span>
                <span>-{order.walletCreditUsed.toFixed(2)} {t("services.currency")}</span>
              </div>
            )}
            {order.cashAmountDue !== undefined && order.cashAmountDue > 0 && (
              <div className="flex justify-between font-medium text-foreground pt-1 border-t">
                <span className="flex items-center gap-1">
                  <CreditCard className="h-3 w-3" />
                  {t("orders.cashDue")}:
                </span>
                <span>{order.cashAmountDue.toFixed(2)} {t("services.currency")}</span>
              </div>
            )}
          </div>
        ) : null}
        {order.notes && (
          <div className="text-sm text-muted-foreground pt-2 border-t">
            <span className="font-medium">{t("orders.notes")}:</span> {order.notes}
          </div>
        )}
        {order.cancellationReason && (
          <div className="text-sm text-destructive pt-2 border-t">
            <span className="font-medium">{t("orders.cancellationReason")}:</span> {order.cancellationReason}
          </div>
        )}

        {/* Payment, Cancel, and Rate Actions */}
        <div className="pt-3 border-t flex flex-wrap gap-2">
          {canCompletePayment && (
            <Button
              size="sm"
              onClick={() => window.location.href = order.paymentLink!}
              data-testid={`button-complete-payment-${order.id}`}
            >
              <CreditCard className="h-4 w-4 me-2" />
              {t("orders.completePayment")}
            </Button>
          )}
          {canCancelOrder &&
            (order.status === 'pending' || order.status === 'confirmed') &&
            onCancelClick &&
            // Mirrors the server's cutoff so the button hides once it's too
            // late; the server enforces the same rule on the call.
            !(order.appointmentDateTime &&
              new Date(order.appointmentDateTime).getTime() < Date.now() + cancelCutoffMinutes * 60 * 1000) && (
            <Button
              size="sm"
              variant="outline"
              className="text-destructive border-destructive/40 hover:bg-destructive/10"
              onClick={() => onCancelClick(order.id)}
              data-testid={`button-cancel-order-${order.id}`}
            >
              <XCircle className="h-4 w-4 me-2" />
              {t("orders.cancelOrder")}
            </Button>
          )}
          {canEditActive && (order.status === 'pending' || order.status === 'confirmed') && onEditClick && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onEditClick(order.id)}
              data-testid={`button-edit-order-${order.id}`}
            >
              <Pencil className="h-4 w-4 me-2" />
              {t("orders.editOrder")}
            </Button>
          )}
          {order.status === 'completed' && !order.reviewId && onRateClick && (
            <Button
              size="sm"
              variant="default"
              onClick={() => onRateClick(order.id)}
              data-testid={`button-rate-order-${order.id}`}
            >
              <Star className="h-4 w-4 me-2" />
              {t("reviews.submitReview")}
            </Button>
          )}
          {order.status === 'completed' && onReorder && !isProductOrder && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onReorder(order.id)}
              data-testid={`button-reorder-${order.id}`}
            >
              <Repeat className="h-4 w-4 me-2" />
              {t("orders.reorder")}
            </Button>
          )}
          {order.paymentStatus === 'paid' && onDownload && (
            <Button
              size="sm"
              variant="outline"
              onClick={async () => {
                setDownloading(true);
                const ok = await onDownload(order.id);
                setDownloading(false);
                if (!ok) {
                  toast({ title: t("paymentSuccess.downloadFailed"), variant: "destructive" });
                }
              }}
              disabled={downloading}
              data-testid={`button-download-invoice-${order.id}`}
            >
              {downloading ? <Loader2 className="h-4 w-4 me-2 animate-spin" /> : <Download className="h-4 w-4 me-2" />}
              {downloading ? t("paymentSuccess.downloading") : t("paymentSuccess.downloadInvoice")}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

interface OrdersListProps {
  orders: Order[] | undefined;
  isLoading: boolean;
  onRateClick?: (orderId: string) => void;
  onDownload?: (id: string) => Promise<boolean>;
  onReorder?: (orderId: string) => void;
  onEditClick?: (orderId: string) => void;
  canEditActive?: boolean;
  onCancelClick?: (orderId: string) => void;
  canCancelOrder?: boolean;
  cancelCutoffMinutes?: number;
}

function OrdersList({ orders, isLoading, onRateClick, onDownload, onReorder, onEditClick, canEditActive, onCancelClick, canCancelOrder, cancelCutoffMinutes }: OrdersListProps) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!orders || orders.length === 0) {
    return (
      <div className="text-center py-12">
        <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium mb-2">{t("orders.noOrders")}</h3>
        <p className="text-muted-foreground mb-6">{t("orders.noOrdersDesc")}</p>
        <Link href="/booking">
          <Button data-testid="button-book-now-empty">
            {t("orders.bookNow")}
            {isRtl ? <ArrowLeft className="ms-2 h-4 w-4" /> : <ArrowRight className="ms-2 h-4 w-4" />}
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {orders.map((order) => (
        <OrderCard
          key={order.id}
          order={order}
          onRateClick={onRateClick}
          onDownload={onDownload}
          onReorder={onReorder}
          onEditClick={onEditClick}
          canEditActive={canEditActive}
          onCancelClick={onCancelClick}
          canCancelOrder={canCancelOrder}
          cancelCutoffMinutes={cancelCutoffMinutes}
        />
      ))}
    </div>
  );
}

export default function Orders() {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [currentPage, setCurrentPage] = useState(1);
  const [reviewDialogOrderId, setReviewDialogOrderId] = useState<string | null>(null);
  const [cancelOrderId, setCancelOrderId] = useState<string | null>(null);
  const cancelMutation = useCancelOrder();

  const pageSize = 10;

  const { data: allOrdersData, isLoading: isLoadingAll } = useOrders({ page: currentPage, limit: pageSize });
  const { data: services } = useServices();
  const { data: publicSettings } = usePublicSettings();
  const canEditActiveOrders = publicSettings?.customerCanEditActiveOrder ?? false;
  const canCancelOrders = publicSettings?.customerCanCancelOrder ?? false;
  const cancelCutoffMinutes = publicSettings?.customerCancelCutoffMinutes ?? 60;

  const allOrders = allOrdersData?.data;
  const pagination = allOrdersData?.pagination;

  const handleRateClick = (orderId: string) => {
    setReviewDialogOrderId(orderId);
  };

  const handleEditClick = (orderId: string) => {
    navigate(`/orders/${orderId}/edit`);
  };

  const handleReorder = (orderId: string) => {
    const order = allOrders?.find((o) => o.id === orderId);
    if (!order) return;
    const cartItems = buildCartFromOrder(order, services);
    if (cartItems.length === 0) return;
    setReorderItems(cartItems);
    navigate("/booking");
  };

  const handleReviewSuccess = () => {
    setReviewDialogOrderId(null);
    // Invalidate orders query to refresh the list
    window.location.reload();
  };

  return (
    <div className="min-h-screen pt-24 pb-24 bg-background">
      <div className="container mx-auto px-4 max-w-3xl">
        <div className="mb-8">
          <h1 className="text-3xl font-bold font-display mb-2">{t("orders.title")}</h1>
          <p className="text-muted-foreground">{t("orders.subtitle")}</p>
        </div>

        <OrdersList
          orders={allOrders}
          isLoading={isLoadingAll}
          onRateClick={handleRateClick}
          onDownload={downloadInvoice}
          onReorder={handleReorder}
          onEditClick={handleEditClick}
          canEditActive={canEditActiveOrders}
          onCancelClick={setCancelOrderId}
          canCancelOrder={canCancelOrders}
          cancelCutoffMinutes={cancelCutoffMinutes}
        />

        <Dialog open={cancelOrderId !== null} onOpenChange={(open) => { if (!open) setCancelOrderId(null); }}>
          <DialogContent dir={i18n.language === 'ar' ? "rtl" : "ltr"}>
            <DialogHeader>
              <DialogTitle>{t("orders.cancelConfirmTitle")}</DialogTitle>
              <DialogDescription>{t("orders.cancelConfirmDesc")}</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setCancelOrderId(null)}
                disabled={cancelMutation.isPending}
                data-testid="button-cancel-keep"
              >
                {t("orders.cancelKeepOrder")}
              </Button>
              <Button
                variant="destructive"
                disabled={cancelMutation.isPending}
                onClick={async () => {
                  if (!cancelOrderId) return;
                  try {
                    await cancelMutation.mutateAsync({ id: cancelOrderId });
                    setCancelOrderId(null);
                    toast({ title: t("orders.cancelSuccess") });
                  } catch (error: any) {
                    setCancelOrderId(null);
                    toast({
                      title: t("orders.cancelFailed"),
                      description: error.message,
                      variant: "destructive",
                    });
                  }
                }}
                data-testid="button-cancel-confirm"
              >
                {cancelMutation.isPending && <Loader2 className="h-4 w-4 me-2 animate-spin" />}
                {t("orders.cancelConfirmAction")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Pagination Controls */}
        {pagination && pagination.totalPages > 1 && (
          <div className="flex items-center justify-center gap-4 mt-6">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage <= 1 || isLoadingAll}
              data-testid="button-prev-page"
            >
              <ArrowLeft className="w-4 h-4 me-1" />
              {t("common.previous")}
            </Button>

            <span className="text-sm text-muted-foreground">
              {t("common.pageOf", { current: currentPage, total: pagination.totalPages })}
            </span>

            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.min(pagination.totalPages, p + 1))}
              disabled={currentPage >= pagination.totalPages || isLoadingAll}
              data-testid="button-next-page"
            >
              {t("common.next")}
              <ArrowRight className="w-4 h-4 ms-1" />
            </Button>
          </div>
        )}
      </div>

      {reviewDialogOrderId && (
        <ReviewDialog
          orderId={reviewDialogOrderId}
          existingReview={reviewDialogOrderId ? allOrders?.find(o => o.id === reviewDialogOrderId)?.review : undefined}
          onSuccess={handleReviewSuccess}
        />
      )}
    </div>
  );
}
